import './RateTable.less';

import Icon, {
  CarOutlined,
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
  DeleteFilled,
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  FrownFilled,
  FrownOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  LoadingOutlined,
  MehOutlined,
  MoreOutlined,
  NodeIndexOutlined,
  PullRequestOutlined,
  SmileFilled,
  SmileOutlined,
  StopOutlined,
  SwapOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Dropdown,
  Form,
  Menu,
  Modal,
  notification,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import { keyBy, maxBy } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { AuthContext } from '../../contexts/authContext';
import { useCostKmStats } from '../../hooks/apiHooks/carrier';
import {
  useRateAssignTerminals,
  useRateCreate,
  useRatesDuplicate,
  useRatesRemove,
  useRatesReverse,
  useRatesUpdate,
  useRatesUpdateCoordinates,
  useRateUpdate,
  useRateUpdateCoordinates,
} from '../../hooks/apiHooks/rate';
import {
  formatDateTime,
  getAlphabet,
  getfuelExpiryString,
  getProvinceFromAddress,
  validateRateClasses,
} from '../../library/util';
import { UserRole } from '../../models/User';
import carrierService from '../../services/carrierService';
import RateDetailsDrawer from '../drawers/RateDetailsDrawer';
import AddBusinessSvg from '../icons/AddBusinessSvg';
import DollarSvg from '../icons/DollarSvg';
import FuelSurchargeSvg from '../icons/FuelSurchargeSvg';
import NoFuelSurchargeSvg from '../icons/NoFuelSurchargeSvg';
import OffRouteSvg from '../icons/OffRouteSvg';
import SettingsEthernetSvg from '../icons/SettingsEthernetSvg';
import LinkRateModal from '../modals/LinkRateModal';
import MapModal from '../modals/MapModal';
import MultipleBaseRateAdjustModal from '../modals/MultipleBaseRateAdjustModal';
import MutipleRateTerminalAssignModal from '../modals/MutipleRateTerminalAssignModal';
import RateTerminalAssignModal from '../modals/RateTerminalAssignModal';
import ShipTypesModal from '../modals/ShipTypesModal';
import ValidateRateClassesModal from '../modals/ValidateRateClassesModal';
import CostPerKmStatsWidget from '../widgets/CostPerKmStatsWidget';
import RateTableEditableCell from './RateTableEditableCell';

/*
export interface IRateTableData {
  carriers: {
    [key: number]: Carrier;
  };
  changedRowCarrierId?: number;
  carrierClassesMap: {
    [key: number]: {
      id: number;
      classes: Class[] | undefined;
    };
  };
  carrierOptionMap: {
    [key: number]: {
      id: number;
      option: Option | undefined;
    };
  };
  carrierModesMap: {
    [key: number]: {
      id: number;
      modes: Mode[] | undefined;
    };
  };
  shipTypes: ShipType[];
  onShowOriginMap: Function;
  onShowDestinationMap: Function;
  isShowingExtraColumns: boolean;
}

interface coords {
  lat?: number;
  lng?: number;
}
*/
const defaultState = {
  carrierClassesMap: {},
  carrierModesMap: {},
  carrierOptionMap: {},
  carriers: [],
  costKmStatsIndex: new Map(),
  isAuditActive: false,
  isShowingExtraColumns: false,
  onShowDestinationMap: () => { },
  onShowOriginMap: () => { },
  shipTypes: [],
};

export const RateTableContext = React.createContext({
  ...defaultState,
  form: null,
});

/*
interface IProps {
  showAllCarriers?: boolean;
  carriers?: Carrier[];
  selectedCarrier?: Carrier;
  shipTypes: ShipType[];
  rates: Rate[];
  isEditable?: boolean;
  loading: boolean;
}
*/

const stringSorter = (a, b) => {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
};

const numberSorter = (a, b) => {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
};

const addressSortString = (city) => {
  return city.split(', ').reverse().join(', ');
};

const RateTable = ({
  allRatesForModals,
  carriers,
  customEmptyText,
  highlightRateId,
  isEditable = true,
  loading,
  onClearFiltersAndGoToRate,
  rates,
  selectedCarrier,
  shipTypes,
  showAllCarriers,
}) => {
  const ratesForLinking = allRatesForModals ?? rates;
  const { authUser } = useContext(AuthContext);
  const createMutation = useRateCreate();
  const updateMutation = useRateUpdate();
  const updateCoordinatesMutation = useRateUpdateCoordinates();
  const assignTerminalsMutation = useRateAssignTerminals();
  const removeMutation = useRatesRemove();
  const reverseMutation = useRatesReverse();
  const duplicateMutation = useRatesDuplicate();
  const bulkUpdateMutation = useRatesUpdate();
  const bulkUpdateCoordinatesMutation = useRatesUpdateCoordinates();

  const [form] = Form.useForm();
  const [editingRate, setEditingRate] = useState();
  const [changedRowCarrierId, setChangedRowCarrierId] = useState();
  const [updatingRateId, setUpdatingRateId] = useState();
  const [validatingRateIds, setValidatingRateIds] = useState([]);
  const [estimatingRateIds, setEstimatingRateIds] = useState([]);
  const [fixingCoordinatesRateIds, setFixingCoordinatesRateIds] = useState([]);
  const [selectedRateIds, setSelectedRateIds] = useState([]);
  const [rateForDetails, setRateForDetails] = useState();
  const [isRemoveConfirm, setIsRemoveConfirm] = useState(false);
  const [isShowingDetails, setIsShowingDetails] = useState(false);
  const [isSingleRemoveConfirm, setIsSingleRemoveConfirm] = useState(false);
  const [isReverseConfirm, setIsReverseConfirm] = useState(false);
  const [isAdjustBaseRates, setIsAdjustBaseRates] = useState(false);
  const [isChangingShipTypes, setIsChangingShipTypes] = useState(false);
  const [isDuplicateConfirm, setIsDuplicateConfirm] = useState(false);
  const [isMultipleTerminalsAssign, setIsMultipleTerminalsAssign] =
    useState(false);
  const [selectedRateTerminalsAssign, setSelectedRateTerminalsAssign] =
    useState(null);
  const [isShowingExtraColumns, setIsShowingExtraColumns] = useState(false);
  const [isAuditActive, setIsAuditActive] = useState(false);
  const [isShowingMiles, setIsShowingMiles] = useState(false);
  const [rateForMap, setRateForMap] = useState(null);
  const [mapCoords, setMapCoords] = useState(null);
  const [rateForValidate, setRateForValidate] = useState(null);
  const [rateForLinkParent, setRateForLinkParent] = useState(null);
  const [isBulkLinkParent, setIsBulkLinkParent] = useState(false);
  const [bulkLinkingRateIds, setBulkLinkingRateIds] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const isSuperAdminUser = useMemo(
    () => authUser?.role === UserRole.SUPER_ADMIN,
    [authUser?.role]
  );

  const { costKmStats } = useCostKmStats(
    selectedCarrier?.id ?? 0,
    !!selectedCarrier
  );

  const costKmStatsIndex = useMemo(() => {
    const index = new Map();
    for (const row of costKmStats ?? []) {
      const key = `${row.orgProv}|${row.destProv}|${row.modeId}|${row.classId}`;
      index.set(key, row);
    }
    return index;
  }, [costKmStats]);

  // must be both not volatile (stddev < 0.6) AND not very low (sampleCount > 4)
  const passesQualityRules = useCallback((row) => {
    if (row.stddev != null && Number(row.stddev) >= 0.6) return false; // volatile
    if (row.sampleCount != null && Number(row.sampleCount) <= 4) return false; // very low
    return true;
  }, []);

  const ratePassesEstimateQuality = useCallback(
    (rate) => {
      if (!costKmStats?.length || !rate) return false;
      const orgProv = getProvinceFromAddress(rate.origin);
      const destProv = getProvinceFromAddress(rate.destination);
      if (!orgProv || !destProv) return false;
      const corridorRows = costKmStats.filter(
        (row) =>
          row.orgProv === orgProv &&
          row.destProv === destProv &&
          row.modeId === rate.modeId
      );
      return corridorRows.some(passesQualityRules);
    },
    [costKmStats, passesQualityRules]
  );

  const keyedRates = useMemo(() => keyBy(rates, (rate) => rate.id), [rates]);

  const allRates = useMemo(() => {
    const parentId = (r) => r.parentRateId ?? r.parent_rate_id;
    const rateMap = new Map(
      rates.map((r) => [r.id, { ...r, children: [] }])
    );
    const roots = [];

    for (const rate of rateMap.values()) {
      const parentRateId = parentId(rate);
      if (parentRateId != null && rateMap.has(parentRateId)) {
        rateMap.get(parentRateId).children.push(rate);
      } else {
        roots.push(rate);
      }
    }

    for (const rate of rateMap.values()) {
      if (rate.children?.length > 0) {
        rate.children.sort((a, b) => {
          const byOrigin = (a.origin || '').localeCompare(b.origin || '');
          if (byOrigin !== 0) return byOrigin;
          return (a.destination || '').localeCompare(b.destination || '');
        });
      } else {
        delete rate.children;
      }
    }

    return roots;
  }, [rates]);

  const allParentRowKeys = useMemo(() => {
    const keys = [];
    const collect = (items) => {
      for (const item of items || []) {
        if (item.children?.length) {
          keys.push(item.id);
          collect(item.children);
        }
      }
    };
    collect(allRates);
    return keys;
  }, [allRates]);

  useEffect(() => {
    setExpandedRowKeys(allParentRowKeys);
  }, [allParentRowKeys]);

  const keyedCarriers = useMemo(
    () =>
      keyBy(
        selectedCarrier ? [selectedCarrier] : carriers,
        (carrier) => carrier.id
      ),
    [carriers, selectedCarrier]
  );

  const carrierModesMap = useMemo(() => {
    if (showAllCarriers) {
      return keyBy(
        carriers?.map((carrier) => ({ id: carrier.id, modes: carrier.modes })),
        (carrier) => carrier.id
      );
    } else {
      if (selectedCarrier) {
        return {
          [selectedCarrier.id]: {
            id: selectedCarrier.id,
            modes: selectedCarrier.modes,
          },
        };
      } else {
        return null;
      }
    }
  }, [carriers, selectedCarrier, showAllCarriers]);

  const carrierClassesMap = useMemo(() => {
    if (showAllCarriers) {
      return keyBy(
        carriers?.map((carrier) => ({
          classes: carrier.classes,
          id: carrier.id,
        })),
        (carrier) => carrier.id
      );
    } else {
      if (selectedCarrier) {
        return {
          [selectedCarrier.id]: {
            classes: selectedCarrier.classes,
            id: selectedCarrier.id,
          },
        };
      } else {
        return null;
      }
    }
  }, [carriers, selectedCarrier, showAllCarriers]);

  const carrierOptionMap = useMemo(() => {
    if (showAllCarriers) {
      return keyBy(
        carriers?.map((carrier) => ({
          id: carrier.id,
          option: carrier.option,
        })),
        (carrier) => carrier.id
      );
    } else {
      if (selectedCarrier) {
        return {
          [selectedCarrier.id]: {
            id: selectedCarrier.id,
            option: selectedCarrier.option,
          },
        };
      } else {
        return null;
      }
    }
  }, [carriers, selectedCarrier, showAllCarriers]);

  const maxClassLength = useMemo(() => {
    if (!showAllCarriers) {
      return selectedCarrier?.classes?.length || 0;
    }
    return (
      maxBy(carriers, (carrier) => carrier.classes?.length)?.classes?.length ||
      0
    );
  }, [carriers, selectedCarrier?.classes?.length, showAllCarriers]);

  const selectedRates = useMemo(
    () =>
      selectedRateIds
        .map((rateId) => keyedRates[rateId])
        .filter((rate) => rate),
    [keyedRates, selectedRateIds]
  );

  // find the carrier id of the first selected rate
  const bulkLinkParentCarrierId = useMemo(
    () => selectedRates[0]?.carrierId,
    [selectedRates]
  );

  // find the common parent rate id for the selected rates
  const bulkLinkParentCommonParentId = useMemo(() => {
    if (selectedRates.length === 0) return undefined;
    const firstParent =
      selectedRates[0]?.parentRateId ?? selectedRates[0]?.parent_rate_id;
    const allSame = selectedRates.every(
      (r) => (r.parentRateId ?? r.parent_rate_id) === firstParent
    );
    return allSame ? firstParent ?? undefined : undefined;
  }, [selectedRates]);

  const hasChildRateSelected = useMemo(
    () =>
      selectedRates.some((r) => r.parentRateId ?? r.parent_rate_id),
    [selectedRates]
  );

  // rates that may be selected as a parent in the modal
  // it's gonna exclude rates that:
  // - are not on the same carrier as the selected rates
  // - are already selected (prevents self-link)
  // - are already a child rate (have a parentRateId), to avoid circular links
  const bulkLinkParentAvailableRates = useMemo(() => {
    if (!bulkLinkParentCarrierId) return [];
    const selectedIds = new Set(selectedRateIds);
    return ratesForLinking.filter(
      (r) =>
        r.carrierId === bulkLinkParentCarrierId &&
        !selectedIds.has(r.id) &&
        !(r.parentRateId ?? r.parent_rate_id)
    );
  }, [bulkLinkParentCarrierId, ratesForLinking, selectedRateIds]);

  const selectedRateTerminals = useMemo(() => {
    return selectedRateTerminalsAssign
      ? (selectedCarrier
        ? selectedCarrier.terminals
        : keyedCarriers[selectedRateTerminalsAssign.carrierId.toString()]
          .terminals) || []
      : [];
  }, [keyedCarriers, selectedCarrier, selectedRateTerminalsAssign]);

  const onEdit = useCallback(
    (row) => {
      form.resetFields();
      form.setFieldsValue(row);
      setEditingRate(row);
    },
    [form]
  );

  const onSave = useCallback(() => {
    try {
      const row = form.getFieldsValue(true);

      // Check if on carrier page and terminals were assigned
      if (
        selectedCarrier &&
        (row.originTerminals || row.destinationTerminals)
      ) {
        assignTerminalsMutation.mutate([
          editingRate.id,
          row.originTerminals || [],
          row.destinationTerminals || [],
          editingRate.carrierId,
        ]);
      }

      const updatedRate = {
        ...editingRate,
        ...row,
      };

      // Remove terminals from updatedRate since they're handled separately
      delete updatedRate.originTerminals;
      delete updatedRate.destinationTerminals;

      const changedClassRates = row.classRate;
      // if there are new prices then collect the new values here from the fields
      // note that the listing with all carriers does not have price fields so we need to skip these
      if (changedClassRates) {
        const newClassRateMap = {};
        for (const [key, rate] of Object.entries(changedClassRates)) {
          if (rate && rate.value) {
            newClassRateMap[key] = { value: Number(rate.value) };
          }
        }

        if (newClassRateMap) {
          updatedRate.classRate = newClassRateMap;
        }
      }

      updateMutation.mutate(updatedRate);
      setEditingRate(undefined);
      setUpdatingRateId(updatedRate.id);
      setChangedRowCarrierId(undefined);
    } catch (err) {
      console.log(err);
    }
  }, [
    editingRate,
    form,
    assignTerminalsMutation,
    selectedCarrier,
    updateMutation,
  ]);

  const cancelEditing = useCallback(() => {
    setEditingRate(undefined);
    setChangedRowCarrierId(undefined);
  }, []);

  const onDuplicateRate = useCallback(
    (rate) => {
      const targetRateId = [rate.id];
      duplicateMutation.mutate([targetRateId, selectedCarrier?.id]);
    },
    [duplicateMutation, selectedCarrier?.id]
  );

  const onReverseDuplicateRate = useCallback(
    (rate) => {
      const targetRateId = [rate.id];
      reverseMutation.mutate([targetRateId, selectedCarrier?.id]);
    },
    [reverseMutation, selectedCarrier?.id]
  );

  const onToggleRateActive = useCallback(
    (rate) => {
      rate.active = !rate.active;
      updateMutation.mutate(rate);
      setUpdatingRateId(rate.id);
    },
    [updateMutation]
  );

  const onShowOriginMap = (rate) => {
    setMapCoords({ lat: rate.originLat, lng: rate.originLng });
    setRateForMap(rate);
  };

  const onShowDestinationMap = (rate) => {
    setMapCoords({ lat: rate.destinationLat, lng: rate.destinationLng });
    setRateForMap(rate);
  };

  const canToggleRateTransitional = (rate) => {
    if (rate.shipTypeId === 1 || rate.destinationShipTypeId === 1) {
      return true;
    } else {
      return false;
    }
  };

  const onToggleRateTransitional = useCallback(
    (rate) => {
      rate.transitional = !rate.transitional;
      updateMutation.mutate(rate);
      setUpdatingRateId(rate.id);
    },
    [updateMutation]
  );

  const onToggleRateSkipFuelSurcharge = useCallback(
    (rate) => {
      rate.skipFuelSurcharge = !rate.skipFuelSurcharge;
      updateMutation.mutate(rate);
      setUpdatingRateId(rate.id);
    },
    [updateMutation]
  );

  const onToggleRateIsOnRoute = useCallback(
    (rate) => {
      rate.isOnRoute = rate.isOnRoute === false ? true : false;
      updateMutation.mutate(rate);
      setUpdatingRateId(rate.id);
    },
    [updateMutation]
  );

  const onTogglePreferred = useCallback(
    (rate) => {
      if (rate.preferred === 1) {
        rate.preferred = 0;
      } else {
        rate.preferred = 1;
      }
      updateMutation.mutate(rate);
      setUpdatingRateId(rate.id);
    },
    [updateMutation]
  );

  const onToggleUnPreferred = useCallback(
    (rate) => {
      if (rate.preferred === -1) {
        rate.preferred = 0;
      } else {
        rate.preferred = -1;
      }
      updateMutation.mutate(rate);
      setUpdatingRateId(rate.id);
    },
    [updateMutation]
  );

  const onUpdateCoordinates = useCallback(
    (rate) => {
      setFixingCoordinatesRateIds((prev) => [...prev, rate.id]);
      updateCoordinatesMutation.mutate(rate.id, {
        onSettled: () => {
          setFixingCoordinatesRateIds((prev) =>
            prev.filter((id) => id !== rate.id)
          );
        },
      });
    },
    [updateCoordinatesMutation]
  );

  const onEstimatePrice = useCallback(
    async (rate) => {
      setEstimatingRateIds([rate.id]);
      try {
        const { estimateConfidence: confidence, estimates } =
          await carrierService.getRateEstimate(rate.carrierId, {
            destination: rate.destination,
            modeId: rate.modeId,
            origin: rate.origin,
          });

        const newClassRate = { ...rate.classRate };
        for (const [classIdStr, estimatedValue] of Object.entries(
          estimates ?? {}
        )) {
          const classId = Number(classIdStr);
          const existing = newClassRate[classId];
          if (!existing?.value || existing?.isEstimate === true) {
            newClassRate[classId] = {
              ...(existing ?? {}),
              estimateConfidence: confidence?.[classIdStr],
              isEstimate: true,
              value: estimatedValue,
            };
          }
        }

        updateMutation.mutate(
          { ...rate, classRate: newClassRate },
          {
            onSettled: () => setEstimatingRateIds([]),
            onSuccess: () =>
              notification.success({ message: 'Prices estimated' }),
          }
        );
      } catch (err) {
        console.error(`Error estimating rate ${rate.id}:`, err);
        notification.error({
          description:
            err?.response?.data?.message ?? 'Failed to get price estimates',
          message: 'Estimate failed',
        });
        setEstimatingRateIds([]);
      }
    },
    [updateMutation]
  );

  const onBulkEstimatePrices = useCallback(async () => {
    if (selectedRates.length === 0) return;
    const ratesToEstimate = selectedRates.filter(ratePassesEstimateQuality);
    if (ratesToEstimate.length === 0) {
      notification.info({
        description:
          'Selected rates have insufficient data for price estimation.',
        message: 'No rates to estimate',
      });
      return;
    }
    const rateIds = ratesToEstimate.map((r) => r.id);
    setEstimatingRateIds(rateIds);
    const successCount = { current: 0 };
    const skippedCount = selectedRates.length - ratesToEstimate.length;
    try {
      for (const rate of ratesToEstimate) {
        try {
          const { estimateConfidence: confidence, estimates } =
            await carrierService.getRateEstimate(rate.carrierId, {
              destination: rate.destination,
              modeId: rate.modeId,
              origin: rate.origin,
            });

          const newClassRate = { ...rate.classRate };
          for (const [classIdStr, estimatedValue] of Object.entries(
            estimates ?? {}
          )) {
            const classId = Number(classIdStr);
            const existing = newClassRate[classId];
            if (!existing?.value || existing?.isEstimate === true) {
              newClassRate[classId] = {
                ...(existing ?? {}),
                estimateConfidence: confidence?.[classIdStr],
                isEstimate: true,
                value: estimatedValue,
              };
            }
          }

          await new Promise((resolve, reject) => {
            updateMutation.mutate(
              { ...rate, classRate: newClassRate },
              {
                onError: reject,
                onSettled: () =>
                  setEstimatingRateIds((prev) =>
                    prev.filter((id) => id !== rate.id)
                  ),
                onSuccess: () => {
                  successCount.current += 1;
                  resolve();
                },
              }
            );
          });
        } catch (err) {
          console.error(`Error estimating rate ${rate.id}:`, err);
          notification.error({
            description:
              err?.response?.data?.message ??
              'Insufficient data to estimate prices.',
            message: `Failed to estimate rate ${rate.id}`,
          });
          setEstimatingRateIds((prev) => prev.filter((id) => id !== rate.id));
        }
      }
      if (successCount.current > 0) {
        const msg =
          skippedCount > 0
            ? `Estimated prices applied to ${successCount.current} rate(s), ${skippedCount} skipped (insufficient data)`
            : `Estimated prices applied to ${successCount.current} rate(s)`;
        notification.success({ message: msg });
      }
    } finally {
      setEstimatingRateIds([]);
    }
  }, [updateMutation, selectedRates, ratePassesEstimateQuality]);

  const onShowDetails = useCallback((rate) => {
    setRateForDetails(rate);
    setIsShowingDetails(true);
  }, []);

  const columns = useMemo(() => {
    const columns = [];
    if (rates.some((r) => r.parentRateId != null)) {
      columns.push({
        ...Table.EXPAND_COLUMN,
        onCell: (record) => {
          const parentId = record?.parentRateId ?? record?.parent_rate_id;
          const handleClick = parentId != null
            ? () => {
              const row = document.querySelector(
                `tr[data-row-key="${parentId}"]`
              );
              if (row) {
                row.scrollIntoView({ behavior: 'smooth', block: 'center' });
                row.classList.add('rate-row-highlight');
                setTimeout(
                  () => row.classList.remove('rate-row-highlight'),
                  5000
                );
              } else {
                onClearFiltersAndGoToRate?.(parentId);
              }
            }
            : null;
          return {
            childRateIcon: parentId != null ? (
              <Tooltip title="Go to Linked Parent">
                <LinkOutlined
                  style={{ color: '#1890ff', cursor: 'pointer' }}
                  onClick={handleClick}
                />
              </Tooltip>
            ) : null,
            isExpandColumn: true,
          };
        },
        width: 48,
      });
    }
    if (showAllCarriers) {
      columns.push({
        dataIndex: 'carrierId',
        editable: true,
        sorter: (a, b) =>
          stringSorter(
            keyedCarriers[a.carrierId]?.name || '',
            keyedCarriers[b.carrierId]?.name || ''
          ),
        title: 'Carrier',
      });
    }
    columns.push(
      ...[
        {
          className: 'rate-table-editable-column',
          dataIndex: 'origin',
          defaultSortOrder: 'ascend',
          editable: true,
          sorter: (a, b) =>
            stringSorter(
              `${addressSortString(a.origin)} ${addressSortString(
                a.destination
              )}`,
              `${addressSortString(b.origin)} ${addressSortString(
                b.destination
              )}`
            ),
          title: 'Origin',
        },
        {
          className: 'rate-table-editable-column',
          dataIndex: 'shipTypeId',
          editable: true,
          sorter: (a, b) =>
            stringSorter(
              shipTypes.find((s) => s.id === a.shipTypeId).name || '',
              shipTypes.find((s) => s.id === b.shipTypeId).name || ''
            ),
          title: 'Orig Type',
        },
        {
          className: 'rate-table-editable-column',
          dataIndex: 'destination',
          editable: true,
          sorter: (a, b) =>
            stringSorter(
              `${addressSortString(a.destination)} ${addressSortString(
                a.origin
              )}`,
              `${addressSortString(b.destination)} ${addressSortString(
                b.origin
              )}`
            ),
          title: 'Destination',
        },
        {
          className: 'rate-table-editable-column',
          dataIndex: 'destinationShipTypeId',
          editable: true,
          sorter: (a, b) =>
            stringSorter(
              shipTypes.find((s) => s.id === a.destinationShipTypeId)?.name ||
              '',
              shipTypes.find((s) => s.id === b.destinationShipTypeId)?.name ||
              ''
            ),
          title: 'Dest Type',
        },
        {
          className: 'rate-table-editable-column',
          dataIndex: 'modeId',
          editable: true,
          sorter: (a, b) => stringSorter(a.modeName ?? '', b.modeName ?? ''),
          title: 'Mode',
        },
        {
          dataIndex: 'distance',
          editable: false,
          isExtra: true,
          render: (cell) =>
            Math.ceil(isShowingMiles ? cell / 1.6 : cell).toLocaleString(),
          sorter: (a, b) => (a.distance ?? 0) - (b.distance ?? 0),
          title: isShowingMiles ? 'Distance (miles)' : 'Distance (kms)',
          width: 100,
        },
        {
          dataIndex: 'transitDays',
          editable: true,
          isExtra: true,
          render: (cell) => cell + ' days',
          sorter: (a, b) => (a.transitDays ?? 0) - (b.transitDays ?? 0),
          title: 'Transit',
          width: 80,
        },
      ]
    );
    if (selectedCarrier) {
      for (let i = 0; i < maxClassLength; i++) {
        const iClass = selectedCarrier?.classes?.[i];
        const vehicles = iClass?.Vehicles?.map((vehicle) => vehicle.name);
        const title = showAllCarriers ? (
          `Class ${getAlphabet(i)}`
        ) : (
          <Tooltip placement="bottom" title={vehicles?.join(', ')}>{iClass?.name}</Tooltip>
        );
        let cellClasses = 'rate-table-editable-column';
        if (i % 2 === 0) {
          cellClasses += ' alt-column';
        }
        columns.push({
          className: cellClasses,
          dataIndex: ['classRate', i, 'value'],
          editable: (record) => !(record.parentRateId ?? record.parent_rate_id),
          sorter: (a, b) => {
            const getSortValue = (record) => {
              const basePrice = record.classRate?.[iClass?.id]?.value ?? 0;
              const surcharge = carrierOptionMap?.[record.carrierId]?.option?.fuelSurcharge ?? record.fuelSurcharge ?? 0;
              if (surcharge && !record.skipFuelSurcharge) {
                return basePrice * ((100 + surcharge) / 100);
              }
              return basePrice;
            };
            return getSortValue(a) - getSortValue(b);
          },
          title,
        });
      }
    }
    if (!selectedCarrier) {
      // Push the classPrice column only when selectedCarrier is false
      columns.push({
        dataIndex: 'classPrice',
        editable: false,
        isExtra: false,
        render: (cell, record) => {
          let resultString = '';
          const classRates = record.classRate;
          const surcharge = record.fuelSurcharge || 0;
          for (const key in classRates) {
            if (classRates.hasOwnProperty(key)) {
              const className = classRates[key].name;
              let classValue = classRates[key].value;
              if (!record.skipFuelSurcharge) {
                classValue = classRates[key].value * ((100 + surcharge) / 100);
              }
              resultString += `${className}: $${classValue.toFixed(2)}\n`;
            }
          }
          const tooltipTitle = resultString ? (
            <pre>{resultString}</pre>
          ) : (
            'No Rates found'
          );
          return (
            <Space style={{ marginLeft: 10 }}>
              <Tooltip title={tooltipTitle}>
                <DollarOutlined
                  style={{ color: '#1890ff' }}
                />
              </Tooltip>
            </Space>
          );
        },
        title: 'Rates',
      });
    }
    columns.push(
      ...[
        {
          align: 'center',
          dataIndex: 'skipFuelSurcharge',
          isExtra: true,
          render: (cell) => {
            return cell && <Icon component={NoFuelSurchargeSvg} style={{ color: '#faad14' }} />;
          },
          title: <Tooltip placement="bottom" title="No Fuel Surcharge"><Icon component={NoFuelSurchargeSvg} /></Tooltip>,
        },
        {
          align: 'center',
          dataIndex: 'transitional',
          isExtra: true,
          render: (cell) => {
            return cell && <Icon component={SettingsEthernetSvg} style={{ color: '#faad14' }} />;
          },
          title: <Tooltip placement="bottom" title="Transitional"><Icon component={SettingsEthernetSvg} /></Tooltip>,
        },
        {
          align: 'center',
          dataIndex: 'isOnRoute',
          isExtra: true,
          render: (cell) => cell === false && <Icon component={OffRouteSvg} style={{ color: '#faad14' }} />,
          title: <Tooltip placement="bottom" title="Off Route"><Icon component={OffRouteSvg} /></Tooltip>,
        },
        {
          dataIndex: 'id',
          editable: false,
          isExtra: true,
          sorter: (a, b) => numberSorter(a.id, b.id),
          title: 'Id',
        },
        {
          dataIndex: 'updatedAt',
          render: (cell, record) => {
            if (
              record.id === updatingRateId ||
              validatingRateIds.includes(record.id) ||
              estimatingRateIds.includes(record.id) ||
              fixingCoordinatesRateIds.includes(record.id) ||
              bulkLinkingRateIds.includes(record.id)
            ) {
              return (
                <Spin
                  indicator={<LoadingOutlined spin style={{ fontSize: 24 }} />}
                />
              );
            }
            if (record.id === editingRate?.id) {
              return (
                <Space style={{ marginLeft: 10 }}>
                  <Tooltip title="Save">
                    <Button
                      icon={<CheckOutlined />}
                      shape="circle"
                      size="small"
                      onClick={onSave}
                    />
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <Button
                      icon={<CloseOutlined />}
                      shape="circle"
                      size="small"
                      onClick={cancelEditing}
                    />
                  </Tooltip>
                </Space>
              );
            }
            return (
              <div className="rate-row-actions">
                <div className="rate-row-date">
                  {new Date(cell)
                    .toLocaleDateString('en-US')
                    .replaceAll('/', '-')}
                  {isSuperAdminUser && !record.active && (
                    <Tooltip title="Hidden from search">
                      <StopOutlined style={{ marginLeft: 10 }} />
                    </Tooltip>
                  )}
                </div>
                <Space className="rate-row-buttons">
                  {selectedCarrier && (
                    <Tooltip title="Edit">
                      <Button
                        htmlType="button"
                        icon={<EditOutlined />}
                        shape="circle"
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          onEdit(record);
                        }}
                      />
                    </Tooltip>
                  )}
                  <Tooltip title="Details">
                    <Button
                      icon={<InfoCircleOutlined />}
                      shape="circle"
                      size="small"
                      onClick={() => onShowDetails(record)}
                    />
                  </Tooltip>
                  <Dropdown
                    getPopupContainer={() => document.body}
                    overlay={
                      <Menu>
                        <Menu.ItemGroup key="carrier-config" title="Actions">
                          <Menu.Item
                            key="0"
                            onClick={() => setSelectedRateTerminalsAssign(record)}
                          >
                            <Button
                              icon={<Icon component={AddBusinessSvg} />}
                              size="small"
                              type="link"
                            >
                              Add Terminal
                            </Button>
                          </Menu.Item>
                          {selectedCarrier && ratePassesEstimateQuality(record) && (
                            <Menu.Item
                              key="12"
                              onClick={() => onEstimatePrice(record)}
                            >
                              <Button
                                icon={<ThunderboltOutlined />}
                                size="small"
                                type="link"
                              >
                                Estimate Prices
                              </Button>
                            </Menu.Item>
                          )}
                          <Menu.Item
                            key="link-parent"
                            onClick={() => setRateForLinkParent(record)}
                          >
                            <Button
                              icon={<LinkOutlined />}
                              size="small"
                              type="link"
                            >
                              Link/Unlink Parent Rate
                            </Button>
                          </Menu.Item>
                          <Menu.Item
                            key="11"
                            onClick={() => onUpdateCoordinates(record)}
                          >
                            <Button
                              icon={<GlobalOutlined />}
                              size="small"
                              type="link"
                            >
                              Update Coordinates
                            </Button>
                          </Menu.Item>
                          {selectedCarrier && (
                            <Menu.Item
                              key="10"
                              onClick={() => setRateForValidate(record)}
                            >
                              <Button
                                icon={<CheckOutlined />}
                                size="small"
                                type="link"
                              >
                                Validate Prices
                              </Button>
                            </Menu.Item>
                          )}
                        </Menu.ItemGroup>
                        <Menu.ItemGroup key="duplicate" title="Duplicate">
                          <Menu.Item
                            key="1"
                            onClick={() => onDuplicateRate(record)}
                          >
                            <Button
                              icon={<CopyOutlined />}
                              size="small"
                              type="link"
                            >
                              Duplicate
                            </Button>
                          </Menu.Item>
                          <Menu.Item
                            key="2"
                            onClick={() => onReverseDuplicateRate(record)}
                          >
                            <Button
                              icon={<SwapOutlined />}
                              size="small"
                              type="link"
                            >
                              Duplicate and Reverse Org/Dest
                            </Button>
                          </Menu.Item>
                        </Menu.ItemGroup>
                        <Menu.ItemGroup key="flags" title="Flags">
                          <Menu.Item
                            key="4"
                            onClick={() => onToggleRateSkipFuelSurcharge(record)}
                          >
                            <Button
                              icon={<Icon component={FuelSurchargeSvg} style={{ marginLeft: -3 }} />}
                              size="small"
                              type={record.skipFuelSurcharge ? 'ghost' : 'link'}
                            >
                              {record.skipFuelSurcharge
                                ? 'Fuel Surcharge - Include'
                                : 'Fuel Surcharge - Skip'}
                            </Button>
                          </Menu.Item>
                          <Menu.Item
                            key="on-route"
                            onClick={() => onToggleRateIsOnRoute(record)}
                          >
                            <Button
                              icon={<NodeIndexOutlined />}
                              size="small"
                              type="link"
                            >
                              {record.isOnRoute === false
                                ? 'Mark as On Route'
                                : 'Mark as Off Route'}
                            </Button>
                          </Menu.Item>
                          <Menu.Item
                            key="5"
                            onClick={() => onTogglePreferred(record)}
                          >
                            <Button
                              icon={
                                record.preferred === 1 ? (
                                  <SmileFilled />
                                ) : (
                                  <SmileOutlined />
                                )
                              }
                              size="small"
                              type="link"
                            >
                              Preferred
                            </Button>
                          </Menu.Item>
                          <Menu.Item
                            key="6"
                            onClick={() => onToggleUnPreferred(record)}
                          >
                            <Button
                              icon={
                                record.preferred === -1 ? (
                                  <FrownFilled />
                                ) : (
                                  <FrownOutlined />
                                )
                              }
                              size="small"
                              type="link"
                            >
                              Un-Preferred
                            </Button>
                          </Menu.Item>
                          <Menu.Item
                            key="3"
                            onClick={() => onToggleRateTransitional(record)}
                          >
                            <Button
                              disabled={!canToggleRateTransitional(record)}
                              icon={<Icon component={SettingsEthernetSvg} />}
                              size="small"
                              type="link"
                            >
                              Transitional
                            </Button>
                          </Menu.Item>
                        </Menu.ItemGroup>
                        {isSuperAdminUser && (
                          <Menu.ItemGroup key="admin" title="Admin">
                            <Menu.Item
                              key="8"
                              onClick={() => onToggleRateActive(record)}
                            >
                              <Button
                                icon={
                                  record.active ? (
                                    <StopOutlined />
                                  ) : (
                                    <PullRequestOutlined />
                                  )
                                }
                                size="small"
                                type="link"
                              >
                                {record.active
                                  ? 'Exclude from search'
                                  : 'Include in search'}
                              </Button>
                            </Menu.Item>
                          </Menu.ItemGroup>
                        )}
                        <Menu.Divider />
                        <Menu.Item
                          key="7"
                          onClick={() => {
                            setSelectedRateIds([record.id]);
                            setIsSingleRemoveConfirm(true);
                          }}
                        >
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            loading={removeMutation.isLoading}
                            type="link"
                          >
                            Remove
                          </Button>
                        </Menu.Item>
                      </Menu>
                    }
                    placement="bottomRight"
                    trigger={['click']}
                  >
                    <Tooltip title="More Actions">
                      <Button
                        icon={<MoreOutlined />}
                        shape="circle"
                        size="small"
                      />
                    </Tooltip>
                  </Dropdown>
                </Space>
              </div>
            );
          },
          title: ' ',
          width: '150px',
        },
      ]
    );
    return columns;
  }, [
    rates,
    showAllCarriers,
    isShowingMiles,
    selectedCarrier,
    keyedCarriers,
    carrierOptionMap,
    shipTypes,
    maxClassLength,
    updatingRateId,
    validatingRateIds,
    estimatingRateIds,
    fixingCoordinatesRateIds,
    bulkLinkingRateIds,
    editingRate?.id,
    onSave,
    cancelEditing,
    isSuperAdminUser,
    removeMutation.isLoading,
    onEdit,
    onShowDetails,
    onToggleRateActive,
    onDuplicateRate,
    onReverseDuplicateRate,
    onToggleRateTransitional,
    onToggleRateSkipFuelSurcharge,
    onToggleRateIsOnRoute,
    onTogglePreferred,
    onToggleUnPreferred,
    onUpdateCoordinates,
    onEstimatePrice,
    ratePassesEstimateQuality,
    onClearFiltersAndGoToRate,
  ]);

  const mergedColumns = useMemo(() => {
    let rawResult = columns.map((col) => {
      if (!col.editable) {
        return col;
      }

      return {
        ...col,
        onCell: (record, rowIndex) => {
          const isEditable =
            typeof col.editable === 'function'
              ? col.editable(record)
              : col.editable;
          return {
            align: col.align,
            dataIndex: col.dataIndex,
            editing: isEditable && record.id === editingRate?.id,
            isExpandColumn: col.isExpandColumn,
            record,
            rowIndex,
            width: col.width,
          };
        },
      };
    });

    // filter out extra columns if we aren't using them
    if (!isShowingExtraColumns) {
      rawResult = rawResult.filter((nextCol) => !nextCol.isExtra);
    }

    return rawResult;
  }, [columns, editingRate?.id, isShowingExtraColumns]);

  const onFieldsChange = useCallback(
    (changedFields, allFields) => {
      if (
        changedFields.length &&
        changedFields[0].name[0] === 'carrierId' &&
        changedFields[0].touched
      ) {
        form.setFields([
          {
            name: 'modeId',
            value: undefined,
          },
        ]);
        setChangedRowCarrierId(changedFields[0].value);
        editingRate.carrierId = changedFields[0].value;
      }
    },
    [form, editingRate]
  );

  const onSelectChange = useCallback((selectedRowKeys) => {
    setSelectedRateIds(selectedRowKeys);
  }, []);

  const onRemoveRates = useCallback(() => {
    setIsRemoveConfirm(false);
    removeMutation.mutate([selectedRateIds, selectedCarrier?.id]);
  }, [removeMutation, selectedCarrier?.id, selectedRateIds]);

  const onReverseRates = useCallback(() => {
    setIsReverseConfirm(false);
    reverseMutation.mutate([selectedRateIds, selectedCarrier?.id]);
  }, [reverseMutation, selectedCarrier?.id, selectedRateIds]);

  const onDuplicateRates = useCallback(() => {
    setIsDuplicateConfirm(false);
    duplicateMutation.mutate([selectedRateIds, selectedCarrier?.id]);
  }, [duplicateMutation, selectedCarrier?.id, selectedRateIds]);

  const onToggleTransitionalRates = useCallback(() => {
    const rates = selectedRateIds.map((id) => keyedRates[id]);
    const isTransitional = rates.every((r) => r.transitional);
    const newRates = rates.map((rate) => ({
      ...rate,
      transitional: !isTransitional,
    }));
    bulkUpdateMutation.mutate([newRates, selectedCarrier?.id]);
  }, [bulkUpdateMutation, keyedRates, selectedCarrier?.id, selectedRateIds]);

  const onToggleIsOnRouteRates = useCallback(() => {
    const rates = selectedRateIds.map((id) => keyedRates[id]);
    const allOnRoute = rates.every((r) => r.isOnRoute !== false);
    const newRates = rates.map((rate) => ({
      ...rate,
      isOnRoute: !allOnRoute,
    }));
    bulkUpdateMutation.mutate([newRates, selectedCarrier?.id]);
  }, [bulkUpdateMutation, keyedRates, selectedCarrier?.id, selectedRateIds]);

  const onBulkUpdatePreferred = useCallback(
    (newPreferred) => {
      const rates = selectedRateIds.map((id) => keyedRates[id]);
      const newRates = rates.map((rate) => ({
        ...rate,
        preferred: newPreferred,
      }));
      bulkUpdateMutation.mutate([newRates, selectedCarrier?.id]);
    },
    [bulkUpdateMutation, keyedRates, selectedCarrier?.id, selectedRateIds]
  );

  const onBulkLinkParent = useCallback(
    async (parentRateId) => {
      setIsBulkLinkParent(false);
      const ids = [...selectedRateIds];
      setBulkLinkingRateIds(ids);
      const parentRate = parentRateId != null ? keyedRates[parentRateId] : null;
      const parentRateIdVal = parentRateId ?? null;
      const classRate = parentRate != null ? { ...(parentRate.classRate ?? {}) } : null;

      try {
        for (const rate of selectedRates) {
          const payload = {
            ...rate,
            classRate: classRate ?? rate.classRate,
            parentRateId: parentRateIdVal,
          };
          await updateMutation.mutateAsync(payload);
          setBulkLinkingRateIds((prev) => prev.filter((id) => id !== rate.id));
        }
      } catch (err) {
        notification.error({
          description: err?.message ?? 'Failed to update parent rate.',
          message: 'Update failed',
        });
        setBulkLinkingRateIds([]);
      } finally {
        setBulkLinkingRateIds([]);
      }
    },
    [keyedRates, selectedRates, selectedRateIds, updateMutation]
  );

  const onBulkValidateRates = useCallback(async () => {
    if (selectedRates.length === 0) {
      return;
    }
    const rateIds = selectedRates.map((rate) => rate.id);
    setValidatingRateIds(rateIds);

    try {
      for (const rate of selectedRates) {
        const classIdsWithRates = Object.keys(rate.classRate)
          .map(Number)
          .filter((classId) => rate.classRate[classId]?.value !== undefined);

        try {
          await validateRateClasses(rate, classIdsWithRates, updateMutation);
          setValidatingRateIds((prev) => prev.filter((id) => id !== rate.id));
        } catch (error) {
          console.error(`Error validating rate ${rate.id}:`, error);
          setValidatingRateIds((prev) => prev.filter((id) => id !== rate.id));
        }
      }
    } finally {
      setValidatingRateIds([]);
    }
  }, [selectedRates, updateMutation]);

  const processBulkUpdateCoordinates = useCallback(
    (ids, carrierId) => {
      if (ids.length === 0) return;
      setFixingCoordinatesRateIds(ids);
      bulkUpdateCoordinatesMutation.mutate([ids, carrierId], {
        onError: (error) => {
          notification.error({
            description: error?.message ?? 'An unexpected error occurred.',
            message: 'Coordinates update failed',
          });
          setFixingCoordinatesRateIds([]);
        },
        onSettled: (data) => {
          if (!data?.remainingIds?.length) {
            setFixingCoordinatesRateIds([]);
          }
        },
        onSuccess: (data) => {
          const remainingIds = data?.remainingIds ?? [];
          const isAllSkipped = data?.skipped === data?.total;

          notification.success({
            description: remainingIds.length > 0
              ? `Unselected those and continuing with ${remainingIds.length} remaining.`
              : isAllSkipped
                ? 'Coordinates were already correct.'
                : undefined,
            message: isAllSkipped
              ? `${data.total} rates skipped`
              : `${data.total} rates updated`,
          });

          if (remainingIds.length > 0) {
            setSelectedRateIds(remainingIds);
            setFixingCoordinatesRateIds(remainingIds);
            setTimeout(() => processBulkUpdateCoordinates(remainingIds, carrierId), 0);
          } else {
            setFixingCoordinatesRateIds([]);
            setSelectedRateIds([]);
          }
        },
      });
    },
    [bulkUpdateCoordinatesMutation]
  );

  const onBulkUpdateCoordinates = useCallback(() => {
    if (selectedRateIds.length === 0) return;
    processBulkUpdateCoordinates(selectedRateIds, selectedCarrier?.id);
  }, [
    processBulkUpdateCoordinates,
    selectedCarrier?.id,
    selectedRateIds,
  ]);

  const onToggleExtraColumns = () => {
    setIsShowingExtraColumns((prev) => {
      const next = !prev;
      if (!next) setIsAuditActive(false);
      return next;
    });
  };

  const onToggleShowingMiles = () => {
    setIsShowingMiles(!isShowingMiles);
  };

  useEffect(() => {
    if (updateMutation.isSuccess || createMutation.isSuccess) {
      setUpdatingRateId(undefined);
    }
  }, [createMutation.isSuccess, updateMutation.isSuccess]);

  useEffect(() => {
    if (removeMutation.isSuccess) {
      setSelectedRateIds([]);
    }
  }, [removeMutation.isSuccess, updateMutation.isSuccess]);

  // scrolling to and highlighting a specific rate
  useEffect(() => {
    if (highlightRateId) {
      // find row element by data-row-key
      const rowElement = document.querySelector(
        `tr[data-row-key="${highlightRateId}"]`
      );
      if (rowElement) {
        rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        rowElement.classList.add('rate-row-highlight');

        // Remove highlight class after animation completes
        const timer = setTimeout(() => {
          rowElement.classList.remove('rate-row-highlight');
        }, 5000);

        return () => clearTimeout(timer);
      }
    }
  }, [highlightRateId]);

  const headerButtonsGeneral = () => {
    return (
      <Space>
        {isShowingExtraColumns && (
          <>
            <Switch
              checked={isAuditActive}
              size="small"
              onChange={(checked) => setIsAuditActive(checked)}
            />
            <span>Show Cost/KM Deviation</span>
          </>
        )}
        {selectedRates.length > 0 && (
          <Dropdown
            overlay={
              <Menu>
                <Menu.ItemGroup key="actions" title="Actions">
                  <Menu.Item
                    key="adjrate"
                    disabled={hasChildRateSelected}
                    onClick={() =>
                      !hasChildRateSelected && setIsAdjustBaseRates(true)
                    }
                  >
                    <Button
                      disabled={hasChildRateSelected}
                      icon={<Icon component={DollarSvg} />}
                      size="small"
                      type="link"
                    >
                      Adjust Base Rates
                    </Button>
                  </Menu.Item>
                  <Menu.Item
                    key="shiptypes"
                    onClick={() => setIsChangingShipTypes(true)}
                  >
                    <Button
                      icon={<CarOutlined />}
                      size="small"
                      type="link"
                    >
                      Change Ship Types
                    </Button>
                  </Menu.Item>
                  {selectedCarrier &&
                    selectedRates.some(ratePassesEstimateQuality) && (
                      <Menu.Item
                        key="estimateprices"
                        onClick={onBulkEstimatePrices}
                      >
                        <Button
                          icon={<ThunderboltOutlined />}
                          loading={estimatingRateIds.length > 0}
                          size="small"
                          type="link"
                        >
                          Estimate Prices
                        </Button>
                      </Menu.Item>
                    )}
                  <Menu.Item
                    key="linkparent"
                    onClick={() => setIsBulkLinkParent(true)}
                  >
                    <Button
                      disabled={
                        selectedRates.length > 1 &&
                        new Set(selectedRates.map((rate) => rate.carrierId))
                          .size > 1
                      }
                      icon={<LinkOutlined />}
                      size="small"
                      type="link"
                    >
                      Link/Unlink Parent Rate
                    </Button>
                  </Menu.Item>
                  {selectedCarrier &&
                    (selectedRates.every(
                      (rate, index, arr) => rate.origin === arr[0].origin
                    ) ||
                      selectedRates.every(
                        (rate, index, arr) =>
                          rate.destination === arr[0].destination
                      )) && (
                      <Menu.Item
                        key="terminal"
                        onClick={() =>
                          selectedRates.length === 1
                            ? setSelectedRateTerminalsAssign(selectedRates[0])
                            : setIsMultipleTerminalsAssign(true)
                        }
                      >
                        <Button
                          icon={<Icon component={AddBusinessSvg} />}
                          size="small"
                          type="link"
                        >
                          Terminal
                        </Button>
                      </Menu.Item>
                    )}
                  <Menu.Item
                    key="updatecoords"
                    onClick={onBulkUpdateCoordinates}
                  >
                    <Button
                      icon={<GlobalOutlined />}
                      loading={bulkUpdateCoordinatesMutation.isLoading}
                      size="small"
                      type="link"
                    >
                      Update Coordinates
                    </Button>
                  </Menu.Item>
                  {selectedCarrier && (
                    <Menu.Item key="validate" onClick={onBulkValidateRates}>
                      <Button
                        icon={<CheckOutlined />}
                        loading={bulkUpdateMutation.isLoading}
                        size="small"
                        type="link"
                      >
                        Validate Rates
                      </Button>
                    </Menu.Item>
                  )}
                </Menu.ItemGroup>
                <Menu.ItemGroup key="duplicate" title="Duplicate">
                  <Menu.Item
                    key="duprev"
                    onClick={() => setIsReverseConfirm(true)}
                  >
                    <Button
                      icon={<SwapOutlined />}
                      loading={reverseMutation.isLoading}
                      size="small"
                      type="link"
                    >
                      Duplicate and Reverse Org/Dest
                    </Button>
                  </Menu.Item>
                  <Menu.Item key="dup" onClick={() => setIsDuplicateConfirm(true)}>
                    <Button
                      icon={<CopyOutlined />}
                      loading={duplicateMutation.isLoading}
                      size="small"
                      type="link"
                    >
                      Duplicate Rates
                    </Button>
                  </Menu.Item>
                </Menu.ItemGroup>
                <Menu.ItemGroup key="flags" title="Flags">
                  <Menu.Item
                    key="resetpreferred"
                    onClick={() => onBulkUpdatePreferred(0)}
                  >
                    <Button
                      icon={<MehOutlined />}
                      size="small"
                      type="link"
                    >
                      Clear Preferred
                    </Button>
                  </Menu.Item>
                  <Menu.Item
                    key="unpreferred"
                    onClick={() => onBulkUpdatePreferred(-1)}
                  >
                    <Button
                      icon={<FrownOutlined />}
                      size="small"
                      type="link"
                    >
                      Mark Un-Preferred
                    </Button>
                  </Menu.Item>
                  <Menu.Item
                    key="preferred"
                    onClick={() => onBulkUpdatePreferred(1)}
                  >
                    <Button
                      icon={<SmileOutlined />}
                      size="small"
                      type="link"
                    >
                      Mark Preferred
                    </Button>
                  </Menu.Item>
                  <Menu.Item
                    key="onroute"
                    onClick={onToggleIsOnRouteRates}
                  >
                    <Button
                      icon={<NodeIndexOutlined />}
                      loading={bulkUpdateMutation.isLoading}
                      size="small"
                      type="link"
                    >
                      Mark On Route
                    </Button>
                  </Menu.Item>
                  <Menu.Item
                    key="trans"
                    onClick={onToggleTransitionalRates}
                  >
                    <Button
                      icon={<Icon component={SettingsEthernetSvg} />}
                      loading={bulkUpdateMutation.isLoading}
                      size="small"
                      type="link"
                    >
                      Transitional
                    </Button>
                  </Menu.Item>
                </Menu.ItemGroup>
                <Menu.Divider />
                <Menu.Item key="remove" onClick={() => setIsRemoveConfirm(true)}>
                  <Button
                    danger
                    icon={<DeleteFilled />}
                    loading={removeMutation.isLoading}
                    size="small"
                    type="link"
                  >
                    Remove Rates
                  </Button>
                </Menu.Item>
              </Menu>
            }
            trigger={['click']}
          >
            <Button>{'Actions on selected rates >'}</Button>
          </Dropdown>
        )}
        {isShowingExtraColumns && (
          <Button
            loading={bulkUpdateMutation.isLoading}
            type="default"
            onClick={onToggleShowingMiles}
          >
            {isShowingMiles ? 'Switch to Kilometres' : 'Switch to Miles'}
          </Button>
        )}
        <Button
          icon={<Icon component={SettingsEthernetSvg} />}
          loading={
            bulkUpdateMutation.isLoading && bulkLinkingRateIds.length === 0
          }
          type="default"
          onClick={onToggleExtraColumns}
        >
          {isShowingExtraColumns ? 'Hide Details' : 'Show Details'}
        </Button>
        {selectedCarrier && (
          <CostPerKmStatsWidget
            carrierId={selectedCarrier.id}
            carrierName={selectedCarrier.name}
          />
        )}
      </Space>
    );
  };

  const headerButtons = () => {
    return headerButtonsGeneral();
  };

  const fuelSurchargeSummary = () => {
    if (selectedCarrier) {
      let surcharge = selectedCarrier?.option?.fuelSurcharge || 0;
      let surchargeExpiryData = selectedCarrier?.option
        ?.fuelSurchargeExpiry
        ? getfuelExpiryString(selectedCarrier?.option?.fuelSurchargeExpiry)
        : null;
      let surchargeTimeStamp = selectedCarrier?.last_surcharge_change
        ? formatDateTime(new Date(selectedCarrier.last_surcharge_change))
        : '';

      const isOldSurcharge = selectedCarrier?.last_surcharge_change
        ? (() => {
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          return new Date(selectedCarrier.last_surcharge_change) < threeMonthsAgo;
        })()
        : false;

      const tooltipContent = (
        <div>
          {surchargeExpiryData && (
            <div>
              Valid until:{' '}
              <span style={surchargeExpiryData.color ? { color: surchargeExpiryData.color } : undefined}>
                {surchargeExpiryData.date}
              </span>
            </div>
          )}
          {surchargeTimeStamp && <div>{surchargeTimeStamp}</div>}
        </div>
      );

      return (
        <Tooltip title={surchargeTimeStamp ? tooltipContent : undefined} >
          <Tag color={isOldSurcharge ? 'orange' : 'blue'}>Fuel surcharge: {surcharge.toFixed(2)}%</Tag>
        </Tooltip>
      );
    } else {
      return null;
    }
  };

  return (
    <RateTableContext.Provider
      value={{
        carrierClassesMap: carrierClassesMap,
        carrierModesMap: carrierModesMap,
        carrierOptionMap: carrierOptionMap,
        carriers: keyedCarriers,
        changedRowCarrierId: changedRowCarrierId,
        costKmStatsIndex: costKmStatsIndex,
        form: form,
        isAuditActive: isAuditActive,
        isShowingExtraColumns: isShowingExtraColumns,
        onShowDestinationMap: onShowDestinationMap,
        onShowOriginMap: onShowOriginMap,
        shipTypes: shipTypes,
      }}
    >
      <Modal
        open={isSingleRemoveConfirm}
        title="Are you sure you want to delete this rate?"
        onCancel={() => setIsSingleRemoveConfirm(false)}
        onOk={() => {
          onRemoveRates();
          setIsSingleRemoveConfirm(false);
        }}
      ></Modal>
      <Modal
        centered
        open={isReverseConfirm}
        title="Duplicate and Reverse"
        onCancel={() => setIsReverseConfirm(false)}
        onOk={() => {
          onReverseRates();
        }}
      >
        <p>
          Are you sure you want to DUPLICATE selected rates and REVERSE origin
          and destination?
        </p>
      </Modal>
      <Modal
        centered
        open={isRemoveConfirm}
        title="Remove Rates"
        onCancel={() => setIsRemoveConfirm(false)}
        onOk={() => {
          onRemoveRates();
        }}
      >
        <p>Are you sure you want to delete selected rates?</p>
      </Modal>
      <Modal
        centered
        open={isDuplicateConfirm}
        title="Duplicate Rates"
        onCancel={() => setIsDuplicateConfirm(false)}
        onOk={() => {
          onDuplicateRates();
        }}
      >
        <p>Are you sure you want to duplicate selected rates?</p>
      </Modal>
      <Card
        className="rate-table"
        extra={headerButtons()}
        title={
          <Space align="center" size="middle">
            <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
              {rates.length} Rates
            </div>
            {fuelSurchargeSummary()}
          </Space>
        }
        type="inner"
      >
        <Form component={false} form={form} onFieldsChange={onFieldsChange}>
          <Table
            columns={mergedColumns}
            components={{
              body: {
                cell: RateTableEditableCell,
              },
            }}
            dataSource={allRates}
            expandable={
              allParentRowKeys.length > 0
                ? {
                  expandedRowKeys,
                  onExpandedRowsChange: setExpandedRowKeys,
                }
                : undefined
            }
            loading={loading}
            locale={{
              emptyText: customEmptyText ? customEmptyText : 'No data',
            }}
            pagination={false}
            rowClassName={(record) => {
              let result = '';
              if (record.children?.length > 0) {
                result = 'row-parent-rate';
              } else if (record.parentRateId ?? record.parent_rate_id) {
                result = 'row-child-rate';
              }
              if (isSuperAdminUser) {
                if (record.preferred === 1) {
                  result += ' row-preferred';
                } else if (record.preferred === -1) {
                  result += ' row-unpreferred';
                }
              }
              return result.trim();
            }}
            rowKey={(rate) => rate.id}
            rowSelection={{
              onChange: onSelectChange,
              selectedRowKeys: selectedRateIds,
              type: 'checkbox',
            }}
            scroll={{ x: 'max-content' }}
            showSorterTooltip={false}
            size="small"
            sticky={true}
            onRow={(record) => {
              if (!isEditable) {
                return {};
              }
              return {
                onDoubleClick: () => {
                  if (editingRate?.id !== record.id) {
                    onEdit(record);
                  }
                },
              };
            }}
          />
        </Form>
      </Card>
      <div style={{ marginTop: 10 }}></div>

      {isMultipleTerminalsAssign && (
        <MutipleRateTerminalAssignModal
          carrier={selectedCarrier}
          rates={selectedRates}
          onClose={() => setIsMultipleTerminalsAssign(false)}
          onSubmit={() => setIsMultipleTerminalsAssign(false)}
        />
      )}

      {selectedRateTerminalsAssign && (
        <RateTerminalAssignModal
          carrier={selectedCarrier}
          rate={selectedRateTerminalsAssign}
          terminals={selectedRateTerminals}
          onClose={() => setSelectedRateTerminalsAssign(null)}
        />
      )}

      {isAdjustBaseRates && (
        <MultipleBaseRateAdjustModal
          carrier={selectedCarrier}
          rates={selectedRates}
          onClose={() => setIsAdjustBaseRates(false)}
          onSubmit={() => setIsAdjustBaseRates(false)}
        />
      )}

      {isChangingShipTypes && (
        <ShipTypesModal
          rates={selectedRates}
          shipTypes={shipTypes}
          onClose={() => setIsChangingShipTypes(false)}
          onSubmit={() => setIsChangingShipTypes(false)}
        />
      )}

      {isShowingDetails && (
        <RateDetailsDrawer
          keyedCarriers={keyedCarriers}
          rate={rateForDetails}
          shipTypes={shipTypes}
          onClose={() => setIsShowingDetails(false)}
        />
      )}

      {rateForMap && mapCoords && (
        <MapModal
          lat={mapCoords.lat}
          lng={mapCoords.lng}
          rate={rateForMap}
          onClose={() => setRateForMap(null)}
        />
      )}

      {rateForValidate && selectedCarrier && (
        <ValidateRateClassesModal
          classes={selectedCarrier.classes || []}
          rate={rateForValidate}
          onClose={() => setRateForValidate(null)}
          onSubmit={() => setRateForValidate(null)}
        />
      )}

      {isBulkLinkParent && (
        <LinkRateModal
          classes={keyedCarriers[bulkLinkParentCarrierId]?.classes}
          fuelSurchargePercent={
            keyedCarriers[bulkLinkParentCarrierId]?.option?.fuelSurcharge
          }
          linkedRateId={bulkLinkParentCommonParentId}
          modes={keyedCarriers[bulkLinkParentCarrierId]?.modes}
          rates={bulkLinkParentAvailableRates}
          shipTypes={shipTypes}
          title={`Set Parent Rate for ${selectedRates.length} Selected Rate${selectedRates.length !== 1 ? 's' : ''}`}
          onClose={() => setIsBulkLinkParent(false)}
          onConfirm={onBulkLinkParent}
        />
      )}

      {rateForLinkParent && (
        <LinkRateModal
          classes={keyedCarriers[rateForLinkParent.carrierId]?.classes}
          fuelSurchargePercent={
            keyedCarriers[rateForLinkParent.carrierId]?.option?.fuelSurcharge
          }
          linkedRateId={
            rateForLinkParent.parentRateId ?? rateForLinkParent.parent_rate_id
          }
          modes={keyedCarriers[rateForLinkParent.carrierId]?.modes}
          rates={ratesForLinking.filter(
            (r) =>
              r.carrierId === rateForLinkParent.carrierId &&
              r.id !== rateForLinkParent.id
          )}
          shipTypes={shipTypes}
          onClose={() => setRateForLinkParent(null)}
          onConfirm={(rateId) => {
            const parentRateId = rateId ?? null;
            const parentRate = rateId != null ? ratesForLinking.find((r) => r.id === rateId) : null;
            const classRate = parentRate != null
              ? { ...(parentRate.classRate ?? {}) }
              : rateForLinkParent.classRate;
            setUpdatingRateId(rateForLinkParent.id);
            updateMutation.mutate(
              { ...rateForLinkParent, classRate, parentRateId },
              {
                onError: (err) => {
                  notification.error({
                    description: err?.message ?? 'Failed to update parent rate.',
                    message: 'Update failed',
                  });
                },
                onSettled: () => {
                  setRateForLinkParent(null);
                  setUpdatingRateId(undefined);
                },
              }
            );
          }}
        />
      )}
    </RateTableContext.Provider>
  );
};

export default RateTable;
